import React from "react";

interface SubmissionStep {
  label: string;
  completed: boolean;
}

interface MetaAdsSubmittingModalProps {
  isOpen: boolean;
  progress: number; // 0 - 100
  steps: SubmissionStep[];
  isSuccess?: boolean; // Whether the submission was successful
  isError?: boolean; // Whether the submission failed
  errorMessage?: string; // Error message to display
  instagramUsername?: string; // Instagram account username for success message
  campaignId?: string; // Campaign ID for reference
  mode?: "create" | "draft" | "update"; // Success message type
  onClose?: () => void; // Callback when modal is closed
}

const MetaAdsSubmittingModal: React.FC<MetaAdsSubmittingModalProps> = ({
  isOpen,
  progress,
  steps,
  isSuccess = false,
  isError = false,
  errorMessage,
  mode = "create",
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">

      {/* Gradient Border Wrapper */}
      <div
        className="relative w-full mx-4"
        style={{
          maxWidth: "800px",
          borderRadius: "40px",
          padding: "1px",
          background:
            "linear-gradient(180deg, rgba(130, 135, 152, 0) 0%, rgba(130, 135, 152, 0.4) 100%)",
        }}
      >
        {/* Actual Modal */}
        <div
          className="relative bg-[#0e0e10] shadow-2xl overflow-hidden"
          style={{
            borderRadius: "40px",
            paddingTop: "64px",
            paddingBottom: "64px",
          }}
        >
          {/* 🔥 Centered Top Gradient */}
          <div
            className="absolute left-1/2 -translate-x-1/2 opacity-80 blur-3xl"
            style={{
              top: "-70px",
              width: "460px",
              height: "140px",
              background:
                "linear-gradient(226.45deg, #1B76FF 12.8%, #2BFAA3 93.65%)",
            }}
          />

          {/* Content */}
          <div className="relative z-10 px-[68px]">
            {isSuccess ? (
              // Success Screen
              <>

                {/* Title based on mode */}
                <h2 className="text-white text-center text-[32px] font-semibold leading-[130%]">
                  {mode === "draft" ? "Draft Saved!" :
                   mode === "update" ? "Campaign Updated!" :
                   "Congratulations!"}
                </h2>

                {/* Message based on mode */}
                <p className="text-white/70 text-center mt-4 text-[16px] leading-[150%]">
                  {mode === "draft" ? (
                    <>
                      Your campaign has been saved as a draft.
                      <br />
                      You can come back anytime to continue or publish it.
                    </>
                  ) : mode === "update" ? (
                    <>
                      Your campaign has been successfully updated.
                      <br />
                      Changes may take a few minutes to take effect.
                    </>
                  ) : (
                    <>
                      Your campaign has been successfully submitted to Facebook and Instagram
                      and is now pending review.
                      <br />
                    </>
                  )}
                </p>

                {/* Additional info for create mode */}
                {mode === "create" && (
                  <span className="block mt-4 text-white text-center text-[16px] ">
                    It may take a few hours before your ads start running!
                  </span>
                )}
                {/* Got It Button */}
                <div className="mt-8 flex justify-center">
                  <div
                    className="inline-flex rounded-full p-[1px] overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(88, 242, 169, 0.2) 0%, #58F2A9 100%)",
                    }}
                  >
                    <button
                      onClick={() => {
                        if (onClose) onClose();
                      }}
                      className="w-[180px] px-12 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105 active:scale-95"
                      style={{
                        background: "linear-gradient(to right, #1C3941, #58F2A9)",
                      }}
                    >
                      Got it!
                    </button>
                  </div>
                </div>


              </>
            ) : isError ? (
              // Error Screen
              <>
                {/* Error Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z" fill="#EF4444" fillOpacity="0.2"/>
                      <path d="M25 15L15 25M15 15L25 25" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>

                <h2 className="text-white text-center text-[32px] font-semibold leading-[130%]">
                  Submission Failed
                </h2>

                <p className="text-white/70 text-center mt-4 text-[16px] leading-[150%]">
                  There was an error submitting your campaign to Facebook and Instagram.
                  <br />
                  {errorMessage && (
                    <span className="block mt-2 text-red-400">
                      {errorMessage}
                    </span>
                  )}
                </p>
                <span className="block mt-4 text-white text-center text-[16px]">
                  Please try again or contact support if the issue persists.
                </span>

                {/* Close Button */}
                <div className="mt-8 flex justify-center">
                  <div
                    className="inline-flex rounded-full p-[1px] overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(239, 68, 68, 0.2) 0%, #EF4444 100%)",
                    }}
                  >
                    <button
                      onClick={() => {
                        if (onClose) onClose();
                      }}
                      className="w-[180px] px-12 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105 active:scale-95"
                      style={{
                        background: "linear-gradient(to right, #7F1D1D, #EF4444)",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            ) : (
              // Progress Screen
              <>
                <h2 className="text-white text-center text-[32px] font-semibold leading-[130%]">
                  Almost There!
                </h2>

                <p className="text-white/70 text-center mt-2 text-[20px] leading-[130%]">
                  Your campaign is being submitted to Facebook and Instagram.
                  <br />
                  This may take a few minutes.
                </p>
              </>
            )}

            {/* Steps - only show during progress */}
            {!isSuccess && !isError && (
              <div className="mt-8 space-y-4 px-[90px]">
                {steps.map((step, index) => {
                  const currentStepIndex = steps.findIndex(s => !s.completed);
                  const isActive = index === currentStepIndex;

                  return (
                    <div key={index} className="flex items-center gap-4">
                      {/* Indicator */}
                      {step.completed ? (
                        <svg width="20.34" height="20.34" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.17 20.34C11.5611 20.34 12.8709 20.0733 14.0996 19.54C15.3281 19.0133 16.4073 18.2816 17.3368 17.3451C18.2728 16.4085 19.0073 15.329 19.5404 14.1063C20.0734 12.8771 20.34 11.5634 20.34 10.1651C20.34 8.77335 20.0734 7.46613 19.5404 6.24345C19.0073 5.01427 18.2728 3.93143 17.3368 2.99491C16.4073 2.05838 15.3249 1.32673 14.0898 0.799947C12.8612 0.266647 11.5514 0 10.1602 0C8.76915 0 7.4593 0.266647 6.23071 0.799947C5.00211 1.32673 3.91979 2.05838 2.98373 2.99491C2.05415 3.93143 1.32285 5.01427 0.789809 6.24345C0.26327 7.46613 0 8.77335 0 10.1651C0 11.5634 0.26327 12.8771 0.789809 14.1063C1.32285 15.329 2.0574 16.4085 2.99348 17.3451C3.92954 18.2816 5.01186 19.0133 6.24046 19.54C7.46905 20.0733 8.7789 20.34 10.17 20.34ZM9.08767 15.0038C8.88615 15.0038 8.70415 14.9615 8.54163 14.8769C8.37911 14.7859 8.22636 14.6494 8.08335 14.4672L5.73343 11.6382C5.64242 11.5146 5.57091 11.3943 5.5189 11.2772C5.4734 11.1537 5.45065 11.0268 5.45065 10.8968C5.45065 10.6236 5.54491 10.3927 5.73343 10.2041C5.92194 10.009 6.15271 9.91148 6.42572 9.91148C6.58824 9.91148 6.7345 9.94725 6.86451 10.0188C6.99452 10.0838 7.12453 10.1976 7.25454 10.3602L9.05842 12.6235L12.988 6.34101C13.222 5.9703 13.5112 5.78495 13.8557 5.78495C14.1158 5.78495 14.3466 5.8695 14.5481 6.03859C14.756 6.20768 14.8601 6.4288 14.8601 6.70195C14.8601 6.82552 14.8341 6.9491 14.7821 7.07266C14.7366 7.19623 14.6781 7.3133 14.6065 7.42386L10.053 14.4672C9.93599 14.6363 9.79297 14.7696 9.62396 14.8673C9.46144 14.9583 9.28268 15.0038 9.08767 15.0038Z" fill="#58F2A9" />
                        </svg>
                      ) : (
                        <span
                          className={`rounded-full flex items-center justify-center
                          ${isActive
                              ? "border-2 border-white/30 animate-spin"
                              : "border-2 border-white/40"
                            }
                        `}
                          style={
                            isActive
                              ? {
                                width: "20.34px",
                                height: "20.34px",
                                borderTopColor: "#ffffff",
                                opacity: 1,
                              }
                              : {
                                width: "20.34px",
                                height: "20.34px",
                                opacity: 1,
                              }
                          }
                        />
                      )}

                      <p
                        className={`text-sm ${step.completed
                          ? "text-white"
                          : isActive
                            ? "text-white"
                            : "text-white/60"
                          }`}
                      >
                        {step.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Progress - only show during progress */}
            {!isSuccess && !isError && (
              <div className="mt-10">
                <div className="h-2 max-w-[400px] mx-auto bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${progress}%`,
                      backgroundColor: "#58F2A9",
                    }}
                  />
                </div>

                <p className="text-center text-white text-[14px] mt-2 font-medium">
                  {progress}% completed
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaAdsSubmittingModal;
